import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  DOCUMENT,
  ElementRef,
  computed,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideMaximize, lucideMinimize, lucidePause, lucidePlay, lucideVolume2, lucideVolumeX } from '@ng-icons/lucide';
import { RangeSliderComponent } from '@lite-workspace/shared/helm-ui/range-slider';
import type { PlaylistLesson } from '../catalog.types';
import { PLAY_DISC } from './coach-card.component';
import { OnboardingMediaService } from './onboarding-media.service';

const SPEEDS = [1, 1.25, 1.5, 1.75, 2] as const;
const BAR_IDLE_MS = 2500;

/** iPhone Safari отдаёт на весь экран только сам `<video>` — у остальных элементов этого метода нет. */
type IosVideo = HTMLVideoElement & { webkitEnterFullscreen?: () => void };

const clock = (seconds: number): string => {
  const s = Math.floor(Number.isFinite(seconds) ? seconds : 0);

  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/**
 * Правая половина библиотеки: урок со снятым роликом ждёт на постере, без ролика — тёмная заглушка.
 * На широком экране кадр занимает всю правую часть окна, на телефоне — полосу 3:2. Ролик вписан целиком,
 * без обрезки — интерфейс в уроке виден весь; свободное место — поля в тон ролика, панель у нижнего края.
 */
@Component({
  selector: 'onboarding-lesson-player',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block md:h-full' },
  imports: [NgIcon, RangeSliderComponent],
  viewProviders: [provideIcons({ lucideMaximize, lucideMinimize, lucidePause, lucidePlay, lucideVolume2, lucideVolumeX })],
  template: `
    <!-- Контекстное меню браузера кадр не отдаёт: из него ролик сохраняется в один клик.
         Кадр в порядке Tab: у <video> без controls фокуса нет, иначе клавиатура до панели не дойдёт. -->
    <div
      #frame
      data-testid="lesson-frame"
      tabindex="0"
      role="group"
      aria-label="Видео урока"
      class="relative aspect-3/2 overflow-hidden bg-stage-lesson outline-none focus-visible:ring-2 md:aspect-auto md:h-full
             focus-visible:ring-inset focus-visible:ring-[var(--acc-line)]"
      [class.h-full]="fill()"
      [style.aspect-ratio]="fill() ? 'auto' : null"
      (contextmenu)="$event.preventDefault()"
      (pointermove)="wake()"
      (pointerdown)="wake()"
      (keydown)="onKey($event)"
      (focusin)="onFocusIn($event)"
      (focusout)="onFocusOut($event)"
    >
      <!-- Keyed by record id: a lesson switch remounts the element, so autoplay fires again by itself. -->
      @for (clip of playable(); track clip.id) {
        <video
          #video
          class="absolute inset-0 size-full object-contain"
          [src]="clip.src"
          [poster]="clip.poster"
          [defaultPlaybackRate]="rate()"
          [playbackRate]="rate()"
          [muted]="muted()"
          [volume]="volume()"
          [autoplay]="autoplay()"
          playsinline
          disablePictureInPicture
          disableRemotePlayback
          controlsList="nodownload noplaybackrate noremoteplayback"
          (click)="togglePlay(video)"
          (dblclick)="toggleFullscreen(video)"
          (play)="playing.set(true)"
          (pause)="playing.set(false)"
          (loadedmetadata)="duration.set(video.duration); time.set(video.currentTime)"
          (timeupdate)="time.set(video.currentTime)"
          (ended)="playedToEnd.emit()"
          (webkitendfullscreen)="systemPlayerClosed.emit()"
        ></video>

        @if (soundOffer()) {
          <!-- Звук и весь экран одним нажатием: телефон отдаёт их ролику только в жесте пользователя. -->
          <button type="button" class="group absolute inset-0" aria-label="Включить звук" (click)="engage(video)">
            <span aria-hidden="true" [class]="playDisc">
              <ng-icon name="lucidePlay" class="text-catalog-title [&>svg]:fill-current" />
            </span>
          </button>
        }

        <!-- Панель своя, а не нативная: только в свою встаёт кнопка скорости, и нет меню «⋮» со скачиванием.
             Живёт внутри кадра — на весь экран уходит кадр, и панель остаётся с ним. Панель лежит на ролике,
             поэтому светлая в обеих темах: бегунок перемотки берёт белый вместо поверхности и акцента темы. -->
        <div
          data-testid="lesson-bar"
          class="absolute inset-x-0 bottom-0 bg-linear-to-t from-[var(--scrim-ctrl-2)] to-transparent px-3 pb-2 pt-8
                 transition-opacity duration-[var(--t-mid)] ease-[var(--ease-smooth)] motion-reduce:transition-none
                 [--acc:var(--on-media)] [--panel:var(--on-media)]"
          [class.opacity-0]="!barShown()"
          [attr.inert]="barShown() ? null : ''"
          (pointerenter)="pointerOnBar.set(true)"
          (pointerleave)="pointerOnBar.set(false); wake()"
        >
          <hlm-range-slider
            ariaLabel="Перемотка"
            [max]="duration()"
            [step]="0.1"
            [value]="time()"
            [trackBackground]="track()"
            (valueChange)="video.currentTime = $event"
          />
          <div class="flex items-center gap-1 text-on-media">
            <button
              type="button"
              [attr.aria-label]="playing() ? 'Пауза' : 'Проиграть'"
              class="grid size-8 place-items-center rounded-[var(--r-s)] outline-none transition-opacity
                     duration-[var(--t-fast)] hover:opacity-80 focus-visible:ring-2 focus-visible:ring-[var(--acc-line)]"
              (click)="togglePlay(video)"
            >
              <ng-icon [name]="playing() ? 'lucidePause' : 'lucidePlay'" size="20" class="[&_svg]:fill-current" />
            </button>
            <span class="ml-1 text-sm tabular-nums">{{ clockLabel() }}</span>
            <button
              type="button"
              [attr.aria-label]="'Скорость воспроизведения: ' + speedLabel()"
              class="ml-auto grid h-8 min-w-8 place-items-center rounded-[var(--r-s)] px-1.5 text-sm font-medium
                     tabular-nums outline-none transition-opacity duration-[var(--t-fast)] hover:opacity-80
                     focus-visible:ring-2 focus-visible:ring-[var(--acc-line)]"
              (click)="nextSpeed()"
            >
              {{ speedLabel() }}
            </button>
            <!-- Громкость — полоска над кнопкой по наведению. Не CDK-оверлей: на весь экран уходит только кадр,
                 слой у body там не виден. Нижний отступ обёртки — мостик, курсор идёт к полоске без закрытия. -->
            <div class="group relative">
              <div
                data-testid="lesson-volume"
                class="invisible absolute bottom-full left-1/2 -translate-x-1/2 pb-4 opacity-0 transition-opacity
                       duration-[var(--t-fast)] motion-reduce:transition-none group-hover:visible group-hover:opacity-100
                       group-has-[:focus-visible]:visible group-has-[:focus-visible]:opacity-100"
              >
                <div class="rounded-[var(--r-s)] border border-[var(--hairline-on-dark)] bg-[var(--scrim-ctrl-2)] px-1.25 py-3.5">
                  <hlm-range-slider
                    class="h-24"
                    orientation="vertical"
                    ariaLabel="Громкость"
                    [max]="1"
                    [step]="0.01"
                    [value]="muted() ? 0 : volume()"
                    [trackBackground]="volumeTrack()"
                    (valueChange)="setVolume($event)"
                  />
                </div>
              </div>
              <button
                type="button"
                [attr.aria-label]="muted() ? 'Включить звук' : 'Выключить звук'"
                class="grid size-8 place-items-center rounded-[var(--r-s)] outline-none transition-opacity
                       duration-[var(--t-fast)] hover:opacity-80 focus-visible:ring-2 focus-visible:ring-[var(--acc-line)]"
                (click)="muted.set(!muted())"
              >
                <!-- Своя иконка на каждое состояние: при смене name на месте прежний знак оставался под новым. -->
                @if (muted()) {
                  <ng-icon name="lucideVolumeX" size="20" />
                } @else {
                  <ng-icon name="lucideVolume2" size="20" />
                }
              </button>
            </div>
            <button
              type="button"
              [attr.aria-label]="fullscreen() ? 'Выйти из полноэкранного режима' : 'На весь экран'"
              class="grid size-8 place-items-center rounded-[var(--r-s)] outline-none transition-opacity
                     duration-[var(--t-fast)] hover:opacity-80 focus-visible:ring-2 focus-visible:ring-[var(--acc-line)]"
              (click)="toggleFullscreen(video)"
            >
              <ng-icon [name]="fullscreen() ? 'lucideMinimize' : 'lucideMaximize'" size="20" />
            </button>
          </div>
        </div>
      } @empty {
        <div class="absolute inset-0 flex flex-col items-center justify-center gap-3">
          <span
            class="grid size-14 place-items-center rounded-full border border-[var(--hairline-on-dark)]
                   bg-[var(--wash-on-dark)]"
          >
            <ng-icon name="lucidePlay" class="ml-0.5 text-xl text-on-media/55" aria-hidden="true" />
          </span>
          <p class="text-sm text-on-media/70">Видео скоро появится</p>
        </div>
      }
      <!-- Обвязка хоста (крестик слоя) живёт в кадре: на весь экран уходит кадр, и она остаётся с ним. -->
      <ng-content />
    </div>
  `,
})
export class LessonPlayerComponent {
  private readonly media = inject(OnboardingMediaService);
  private readonly document = inject(DOCUMENT);

  readonly lesson = input.required<PlaylistLesson>();
  /** Заводить ролик сразу: ставит хост, и только когда фотограф позвал именно его (R41). */
  readonly autoplay = input(false);
  /** Ролик досмотрен до конца — по этому событию урок отмечается просмотренным. */
  readonly playedToEnd = output<void>();
  /** Кадр на всю высоту хоста вместо 3:2, ролик вписан целиком. */
  readonly fill = input(false);
  /** Старт без звука: поверх ролика диск, нажатие на него включает звук и весь экран. */
  readonly startMuted = input(false);
  /** iPhone закрыл системный плеер, в который ролик ушёл на весь экран. */
  readonly systemPlayerClosed = output<void>();

  private readonly frame = viewChild.required<ElementRef<HTMLElement>>('frame');
  private readonly video = viewChild<ElementRef<HTMLVideoElement>>('video');

  /** Скорость и звук живут в плеере, а не в ролике: смена урока пересоздаёт видео, выбор остаётся. */
  protected readonly rate = signal<number>(1);
  protected readonly muted = linkedSignal(() => this.startMuted());
  /** Последний ненулевой уровень: полоска в ноль выключает звук, а включение возвращает этот уровень. */
  protected readonly volume = signal(1);
  protected readonly soundOffer = linkedSignal(() => this.startMuted());
  protected readonly playDisc = PLAY_DISC;

  protected readonly fullscreen = signal(false);
  private readonly awake = signal(false);
  /** Курсор стоит на панели — иначе через паузу она станет inert, и следующий клик уйдёт в ролик. */
  protected readonly pointerOnBar = signal(false);
  /** Фокус с клавиатуры внутри кадра; фокус от клика мышью панель не держит, она прячется как обычно. */
  protected readonly focusHeld = signal(false);
  private idleTimer: ReturnType<typeof setTimeout> | undefined;

  /** Ролика в манифесте нет — список пуст, и вместо элемента рисуется заглушка. */
  protected readonly playable = computed(() => {
    const id = this.lesson().id;
    const media = this.media.media(id);

    return media ? [{ id, src: media.clip, poster: media.poster }] : [];
  });

  /** Меняется ровно тогда, когда `@for` пересоздаёт `<video>`: другой урок или ролик появился/исчез. */
  private readonly clipId = computed(() => this.playable()[0]?.id);

  // Состояние ролика живёт в плеере, а `<video>` на каждый урок свой: `pause` снятого элемента браузер шлёт
  // отдельной задачей, когда слушателей уже нет. Поэтому смена ролика сбрасывает состояние сама, не ждёт событий.
  protected readonly playing = linkedSignal<string | undefined, boolean>({ source: this.clipId, computation: () => false });
  /**
   * Ролик идёт прямо сейчас — по событиям самого `<video>`: список рисует по нему паузу на кадре строки.
   * Список спрашивает раньше, чем плеер получил урок: пока `<video>` нет, ответ «нет», урок не читается.
   */
  readonly isPlaying = computed(() => this.video() !== undefined && this.playing());
  protected readonly time = linkedSignal<string | undefined, number>({ source: this.clipId, computation: () => 0 });
  protected readonly duration = linkedSignal<string | undefined, number>({ source: this.clipId, computation: () => 0 });

  /** Как у нативной панели: на паузе видна всегда, во время показа прячется, пока мышь стоит вне панели и фокуса нет. */
  protected readonly barShown = computed(
    () => !this.soundOffer() && (!this.playing() || this.awake() || this.pointerOnBar() || this.focusHeld()),
  );

  protected readonly speedLabel = computed(() => `${String(this.rate()).replace('.', ',')}×`);
  protected readonly clockLabel = computed(() => `${clock(this.time())} / ${clock(this.duration())}`);
  protected readonly track = computed(() => {
    const played = this.duration() > 0 ? (this.time() / this.duration()) * 100 : 0;

    return `linear-gradient(to right, var(--on-media) ${played}%, var(--track-on-dark) ${played}%)`;
  });

  protected readonly volumeTrack = computed(() => {
    const level = this.muted() ? 0 : this.volume() * 100;

    return `linear-gradient(to top, var(--on-media) ${level}%, var(--track-on-dark) ${level}%)`;
  });

  constructor() {
    const onChange = (): void => this.fullscreen.set(this.document.fullscreenElement === this.frame().nativeElement);
    this.document.addEventListener('fullscreenchange', onChange);
    inject(DestroyRef).onDestroy(() => {
      this.document.removeEventListener('fullscreenchange', onChange);
      clearTimeout(this.idleTimer);
    });
  }

  /** Клик по скорости — следующий шаг; после 2× круг начинается снова с 1×. */
  protected nextSpeed(): void {
    const index = SPEEDS.indexOf(this.rate() as (typeof SPEEDS)[number]);
    this.rate.set(SPEEDS[(index + 1) % SPEEDS.length]);
  }

  protected setVolume(level: number): void {
    this.muted.set(level === 0);
    if (level > 0) this.volume.set(level);
  }

  protected wake(): void {
    this.awake.set(true);
    clearTimeout(this.idleTimer);
    this.idleTimer = setTimeout(() => this.awake.set(false), BAR_IDLE_MS);
  }

  protected onFocusIn(event: FocusEvent): void {
    this.focusHeld.set((event.target as Element).matches(':focus-visible'));
    this.wake();
  }

  /** Панель стала inert с кнопкой в фокусе — фокус снимает браузер, не фотограф: будить панель по этому нечем. */
  protected onFocusOut(event: FocusEvent): void {
    this.focusHeld.set(false);
    if (event.relatedTarget === null && (event.target as Element).closest('[inert]')) return;
    this.wake();
  }

  /** Пробел или K ставят паузу, только когда фокус на самом кадре: кнопки панели нажимаются своим пробелом. */
  protected onKey(event: KeyboardEvent): void {
    this.wake();
    const video = this.video()?.nativeElement;

    if (!video || event.target !== event.currentTarget || (event.code !== 'Space' && event.code !== 'KeyK')) return;
    event.preventDefault();
    this.togglePlay(video);
  }

  /** Пауза или пуск снаружи — нажатием по строке играющего урока в списке (T8.7). Ролика нет — ничего. */
  togglePlayback(): void {
    const video = this.video()?.nativeElement;
    if (video) this.togglePlay(video);
  }

  protected togglePlay(video: HTMLVideoElement): void {
    if (video.paused) void video.play()?.catch((): void => undefined);
    else video.pause();
  }

  protected engage(video: IosVideo): void {
    this.soundOffer.set(false);
    this.muted.set(false);
    // Привязка [muted] применится уже после жеста — тогда iPhone звук не отдаст.
    video.muted = false;
    if (video.paused) void video.play()?.catch((): void => undefined);
    if (!this.document.fullscreenElement) this.toggleFullscreen(video);
  }

  protected toggleFullscreen(video: IosVideo): void {
    const frame = this.frame().nativeElement;

    if (this.document.fullscreenElement) void this.document.exitFullscreen();
    else if (frame.requestFullscreen) void frame.requestFullscreen().catch((): void => undefined);
    else video.webkitEnterFullscreen?.();
  }
}
